import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useCallback, useState } from "react";

/**
 * One snapshot from the assessment metrics subcollection, normalized for
 * the sparkline surface. Backends keep IAM and AI inventory counts on the
 * same per-job document, so we fold them both into this shape.
 */
export type InventorySnapshot = {
  readonly timestamp: number;
  readonly jobId: string;
  readonly status: number;
  readonly owner: number;
  readonly agentsConnectors: number;
  readonly risks: number;
};

type MetricsResponse = {
  snapshots: ReadonlyArray<{
    jobId: string;
    requestedTimestamp: string;
    aiInventory?: {
      agents: number;
      managedConnectors: number;
      unmanagedConnectors: number;
      risks: number;
    };
  }>;
};

const normalize = (
  entry: MetricsResponse["snapshots"][number]
): InventorySnapshot => {
  const ai = entry.aiInventory;
  const agents = ai?.agents ?? 0;
  const managed = ai?.managedConnectors ?? 0;
  const unmanaged = ai?.unmanagedConnectors ?? 0;
  // Status in v1 = total AI agents under management; owner metric is not
  // yet emitted by the backend, so mirror agents until ownership lands.
  return {
    timestamp: new Date(entry.requestedTimestamp).getTime(),
    jobId: entry.jobId,
    status: agents,
    owner: agents,
    agentsConnectors: agents + managed + unmanaged,
    risks: ai?.risks ?? 0,
  };
};

export type UseInventoryHistoryArgs = {
  assessmentId: string;
  /**
   * How many snapshots to request from the backend. Capped at 200 server-side.
   */
  limit?: number;
};

/**
 * Loads the per-assessment metrics subcollection from the backend and
 * returns it in chronological order (oldest → newest), ready to feed a
 * TrendSparkline. Fires exactly once per assessmentId change.
 */
export const useInventoryHistory = ({
  assessmentId,
  limit = 60,
}: UseInventoryHistoryArgs): InventorySnapshot[] => {
  const authFetch = useAuthFetch();
  const [snapshots, setSnapshots] = useState<InventorySnapshot[]>([]);

  useGuardedEffect(
    (cancellation) => async () => {
      const url = `assessment/${encodeURIComponent(assessmentId)}/metrics?limit=${limit}`;
      const res = await authFetch(url, {
        method: "GET",
        cancellation,
        onNotOk: (response) => {
          if (import.meta.env.MODE !== "production") {
            // eslint-disable-next-line no-console
            console.warn(
              "[AIInventory] metrics fetch failed",
              response.status,
              url
            );
          }
          return undefined;
        },
      });
      if (!res || cancellation.isCancelled) return;
      const body = (await res.json()) as MetricsResponse;
      if (cancellation.isCancelled) return;
      setSnapshots(body.snapshots.map(normalize));
    },
    [assessmentId, limit, authFetch],
    useCallback(() => setSnapshots([]), [])
  );

  return snapshots;
};
